import styles from "./ScheduleConfirmBookPopup.module.scss";
import moment from "moment/moment";
import CustomButton from "../../../components/UI/CustomButton/CustomButton";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import useAppSelector from "../../../hooks/useAppSelector";
import {IClassEvent, IGetDropInPriceRequest} from "../../../models/ClassEvent";
import {ClassEventService} from "../../../services/classEvent.service";
import {openMessage} from "../../../store/notification/notificationSlice";
import {PurchaseTypes} from "../../../models/Payment";
import {useNavigate} from "react-router-dom";
import useAppDispatch from "../../../hooks/useAppDispatch";
import StripeElements from "../../../components/StripeElements/StripeElements";
import PaymentMethodsPopup from "../../../components/PaymentMethodsPopup/PaymentMethodsPopup";
import CouponForm from "../../../components/CouponForm/CouponForm";
import {ICoupon, ICouponError} from "../../../models/Coupon";
import LoyaltyPointsExchanges from "../../../components/LoyaltyPointsExchanges/LoyaltyPointsExchanges";
import useDisableScroll from "../../../hooks/useDisableScroll";

interface ScheduleConfirmBookingPopupProps {
    showConfirmBookingPopup: {
        show: boolean,
        classDetails: any,
        myReservation: any,
        myDropIn: any
    };
    closeConfirmBookingPopup: () => void;
    makeReload: () => void;
}


const ScheduleConfirmBookPopup: React.FC<ScheduleConfirmBookingPopupProps> = ({showConfirmBookingPopup, closeConfirmBookingPopup, makeReload}) => {
    useDisableScroll();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {gymSettings} = useAppSelector(state => state.gymSettingsSlice);
    const {profile} = useAppSelector(state => state.profileSlice);
    const [coupon, setCoupon] = useState<ICoupon | ICouponError | {}>({});
    const [loyaltyExchangeId, setLoyaltyExchangeId] = useState<number | null>(null);
    const [dropInPrice, setDropInPrice] = useState(0);

    const allowDropIn = showConfirmBookingPopup.classDetails.hasNoSubscription && !showConfirmBookingPopup.myReservation && !showConfirmBookingPopup.myDropIn;

    const [showPaymentMethodPopup, setShowPaymentMethodPopup] = useState<{
        visible: boolean,
        orderParams: any
    }>({visible: false, orderParams: null});

    useEffect(() => {
        getDropInPrice();
    }, [coupon, loyaltyExchangeId]);

    const clickOnDropIn = async (classEvent: IClassEvent) => {
        if (!profile) return;
        try {
            let couponCode = null;
            if ("couponCode" in coupon && coupon.couponCode) {
                couponCode = coupon.couponCode;
            }
            if (couponCode && loyaltyExchangeId) {
                dispatch(openMessage({title: t("YOU_CAN_NOT_APPLY_BOTH_DISCOUNTS"), type: "error"}));
                return;
            }
            const response = await ClassEventService.addDropIn(classEvent.id, profile.customerId, showConfirmBookingPopup.classDetails.dropInPrice, couponCode, loyaltyExchangeId);
            if (response.data.success === false && response.data.error) {
                dispatch(openMessage({title: t(response.data.error.message), type: "error"}));
                return;
            }
            dispatch(openMessage({title: t("YourDropInHasBeenSuccessfullyRegistered"), type: "success"}));
            closeConfirmBookingPopup();
            makeReload();
        } catch (e) {
            dispatch(openMessage({type: "error", title: t("SomethingWentWrong")}));
        }
    }

    const clickOnPayOnline = (classEvent: IClassEvent) => {
        let couponCode = null;
        if ("couponCode" in coupon && coupon.couponCode) {
            couponCode = coupon.couponCode;
        }
        const orderParams = {
            classEventId: classEvent.id,
            returnUrl: `${window.location.protocol}//${window.location.hostname}/schedule`,
            friends: [],
            type: PurchaseTypes.Dropin,
            couponCode,
            loyaltyExchangeId
        };
        setShowPaymentMethodPopup({visible: true, orderParams});
    }

    const hidePaymentsPopup = () => {
        setShowPaymentMethodPopup({visible: false, orderParams: null})
    }

    const clickOnBookNowOrWaitingList = async (eventId: number) => {
        if (!profile) return;
        try {
            const customerId = profile.customerId;
            const response = await ClassEventService.addMemberToEvent([eventId], customerId);
            if (response.data.isOnWaitingList) {
                dispatch(openMessage({
                    type: "success",
                    title: t("YouWereRegisteredSuccessfullyToTheWaitingList")
                }))
            } else {
                dispatch(openMessage({
                    type: "success",
                    title: t("YouWereRegisteredToTheClassSuccessfully")
                }))
            }
            makeReload();
            closeConfirmBookingPopup();
        } catch (e: any) {
            const res = e.response.data
            if (res) {
                const errOb = JSON.parse(e.response.data);
                if (!errOb) {
                    dispatch(openMessage({type: "error", title: t("SomethingWentWrong")}));
                    return;
                }
                dispatch(openMessage({type: "error", title: t(errOb.message, {hours: errOb.hours})}));
            } else {
                dispatch(openMessage({type: "error", title: t("SomethingWentWrong")}))
            }
        }

    }

    const getDropInPrice = async () => {
        try {
            let couponCode = null;
            if ("couponCode" in coupon && coupon.couponCode) {
                couponCode = coupon.couponCode;
            }
            const model: IGetDropInPriceRequest = {
                couponCode,
                loyaltyExchangeId,
                classEventId: showConfirmBookingPopup.classDetails.id
            }
            const response = await ClassEventService.getDropInPrice(model);

            if (response.data.success === false && response.data.error) {
                dispatch(openMessage({type: "error", title: t(response.data.error.message)}))
            } else {
                setDropInPrice(response.data.prices.total);
            }

        } catch (e) {}
    }

    return (
        <>
            <div className={styles.popupContainer}>
                <div className={styles.popup}>
                    <div className={styles.header}>
                        <h3>{t("ConfirmYourBooking")}</h3>
                    </div>
                    <div className={styles.content}>
                        <div className={styles.listsWrapper}>
                            <ul className={styles.list}>
                                <li className={styles.listItem}>
                                    <p className={styles.name}>{t("Class")}</p>
                                    <p className={styles.info}>{showConfirmBookingPopup.classDetails.className}</p>
                                </li>
                                <li className={styles.listItem}>
                                    <p className={styles.name}>{t("Instructor")}</p>
                                    <p className={styles.info}>{showConfirmBookingPopup.classDetails.firstname} {showConfirmBookingPopup.classDetails.lastname}</p>
                                </li>
                                <li className={styles.listItem}>
                                    <p className={styles.name}>{t("Date")}</p>
                                    <p className={styles.info}>{moment(showConfirmBookingPopup.classDetails.occuranceDate).format("DD MMMM YYYY")}</p>
                                </li>
                            </ul>
                            <ul className={styles.list}>
                                <li className={styles.listItem}>
                                    <p className={styles.name}>{t("Time")}</p>
                                    <p className={styles.info}>{moment(showConfirmBookingPopup.classDetails.occuranceDate).format("LT")}</p>
                                </li>
                                <li className={styles.listItem}>
                                    <p className={styles.name}>{t("Venue")}</p>
                                    <p className={styles.info}>{showConfirmBookingPopup.classDetails.trainningRoomName}</p>
                                </li>
                                <li className={styles.listItem}>
                                    <p className={styles.name}>{t("Price")}</p>
                                    <p className={styles.info}>{gymSettings?.defaultCurrencyCode}{allowDropIn ? dropInPrice : showConfirmBookingPopup.classDetails.dropInPrice}</p>
                                </li>
                            </ul>
                        </div>

                        {/* without sub drop in allowed */}
                        {allowDropIn && showConfirmBookingPopup.classDetails.allowDropIn && (
                            <div className={styles.coupon}>
                                <CouponForm
                                    coupon={coupon}
                                    setCoupon={setCoupon}
                                    scope={"dropIn"}
                                />
                            </div>
                        )}
                        {/* without sub drop in allowed */}
                        {allowDropIn && showConfirmBookingPopup.classDetails.allowDropIn && (
                            <div className={styles.loyalty}>
                                <LoyaltyPointsExchanges
                                    itemPrice={showConfirmBookingPopup.classDetails.dropInPrice}
                                    setActiveLoyaltyExchangeId={setLoyaltyExchangeId}
                                    activeLoyaltyExchangeId={loyaltyExchangeId}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.btns}>
                        {/* without sub drop in allowed */}
                        {allowDropIn && (
                            <>
                                {
                                    showConfirmBookingPopup.classDetails.allowDropIn && !showConfirmBookingPopup.classDetails.prepay && (
                                        <CustomButton
                                            text={t("DropIn")}
                                            type={"button"}
                                            typeStyle={"fill"}
                                            borderRadius={false}
                                            disabled={showConfirmBookingPopup.classDetails.placesLeft === 0}
                                            functionOnClick={() => clickOnDropIn(showConfirmBookingPopup.classDetails)}
                                        />
                                    )
                                }
                                {
                                    showConfirmBookingPopup.classDetails.allowDropIn && (
                                        <CustomButton
                                            text={t("PayOnline")}
                                            type={"button"}
                                            typeStyle={"outline"}
                                            blueForOutline={true}
                                            borderRadius={false}
                                            disabled={showConfirmBookingPopup.classDetails.placesLeft === 0}
                                            functionOnClick={() => clickOnPayOnline(showConfirmBookingPopup.classDetails)}
                                        />
                                    )
                                }
                                {
                                    showConfirmBookingPopup.classDetails.notOnlyDropIn && (
                                        <CustomButton
                                            text={t("BuySubscription")}
                                            type={"button"}
                                            typeStyle={"outline"}
                                            blueForOutline={true}
                                            borderRadius={false}
                                            functionOnClick={() => navigate("/plans")}
                                        />
                                    )
                                }
                            </>
                        )}
                        {/* without sub */}
                        {
                            !showConfirmBookingPopup.myReservation && showConfirmBookingPopup.classDetails.canRegister && !showConfirmBookingPopup.myDropIn && showConfirmBookingPopup.classDetails.placesLeft !== 0 && (
                                <CustomButton
                                    text={t("BookNow")}
                                    type={"button"}
                                    typeStyle={"fill"}
                                    borderRadius={false}
                                    functionOnClick={() => clickOnBookNowOrWaitingList(showConfirmBookingPopup.classDetails.id)}
                                />
                            )
                        }
                        {
                            !showConfirmBookingPopup.myReservation && showConfirmBookingPopup.classDetails.canRegister && !showConfirmBookingPopup.myDropIn && showConfirmBookingPopup.classDetails.placesLeft === 0 && showConfirmBookingPopup.classDetails.hasWaitingList && (
                                <CustomButton
                                    text={t("RegisterToWaitingList")}
                                    type={"button"}
                                    typeStyle={"fill"}
                                    borderRadius={false}
                                    functionOnClick={() => clickOnBookNowOrWaitingList(showConfirmBookingPopup.classDetails.id)}
                                />
                            )
                        }
                        <button
                            className={styles.btn}
                            onClick={closeConfirmBookingPopup}
                        >
                            {t("Cancel")}
                        </button>

                    </div>
                </div>
            </div>
            {
                showPaymentMethodPopup.visible && (
                    <StripeElements>
                        <PaymentMethodsPopup
                            closePopupFn={hidePaymentsPopup}
                            functionOnContinue={() => {
                                hidePaymentsPopup()
                                closeConfirmBookingPopup()
                            }}
                            purchasingType={PurchaseTypes.Dropin}
                            orderParams={showPaymentMethodPopup.orderParams}
                        />
                    </StripeElements>
                )
            }
        </>

    )
}

export default ScheduleConfirmBookPopup;